import React, { useState, useEffect, useRef, useCallback, createElement } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable, Icon, Icon3 } from '../lib/react-native-compat';
import { updateUserDataArray as _updateUserDataArray, deleteUserData as _deleteUserData, loadUserData as _loadUserData, f, fNum, pNum, sum, cN, effectiveIsProjectCoordinator, effectiveIsBuyer } from '../db/company';
import { selectedJobAtom, jobUserDataAtom, jobCostDetailAtom, selectedJobDataAtom, selectedVersionAtom, projectCompleteAtom, startDateActualAtom, dataSourceVersionAtom, isLiveVersionAtom, jobPhaseCodesAtom, selectedVersionLockedAtom, userNameAtom, userIdAtom, userEmailCheckAtom, equipmentRatesAtom, equipmentAssetsAtom, userRoleAtom, previewRoleAtom } from '../store/company';
import { useApiTracker } from '../store/useApiStatus';
import { useAtom } from 'jotai';
import moment from 'moment';
import { NumberInput } from './numberInput';
import { s, colors, headerCell, valueCell, valueCellAlt, pastWeekCell, weekDateHeader, stickyHeader, phaseCard, expandButton, sectionDivider, phaseCollapseHeader, editIndicator } from './burnToolPhasedStyles';
import { EquipmentIcon } from './equipment/EquipmentIcon';
import EquipmentDashboard from './burnToolPhasedEquipmentDashboard';
import { ResourcePlanTable } from './manpowerPlanning';
import { getEquipmentIconKey } from './equipment/equipmentIconMapping';
import AuditHistoryPanel from './auditHistoryPanel';

const TAB = 'Burn Tool Equipment Phased';
const COST_TYPE = '52100';
const COST_TYPE_MIN = '52100';
const COST_TYPE_MAX = '52199';
const TPAY = 'X00-TPAY';

// Display names for the equipment categories the icon mapping resolves —
// level 0 of the Resource Plan tree (category → classification → unit).
const EQUIP_CATEGORY_LABELS = {
    airCompressor: 'Air Compressors', attachment: 'Attachments', atv: 'ATVs', backhoe: 'Backhoes',
    container: 'Containers', directionalDrill: 'Directional Drills', dozer: 'Dozers', excavator: 'Excavators',
    loader: 'Loaders', forklift: 'Forklifts', fusionMachine: 'Fusion Machines', tractor: 'Tractors',
    generator: 'Generators', grader: 'Graders', manLift: 'Man Lifts', misc: 'Miscellaneous',
    welder: 'Welders', pipelayer: 'Pipelayers', crawlerCarrier: 'Crawler Carriers', pressureWasher: 'Pressure Washers',
    pressurePump: 'Pressure Pumps', mower: 'Mowers', compactor: 'Compactors', skidSteer: 'Skid Steers',
    suv: 'SUVs', threadingMachine: 'Threading Machines', truck: 'Trucks', waterTruck: 'Water Trucks',
    trailer: 'Trailers', trencher: 'Trenchers', toolVan: 'Tool Vans', default: 'Other Equipment',
};

const DateInput = ({ value, onValueChange, onBlur, style }) => {
    const [date, setDate] = useState(value);
    return createElement('input', {
        type: 'date',
        value: date instanceof Date && date != 'Invalid Date' ? date?.toISOString().split("T")[0] : date,
        onChange: (event) => { setDate(new Date(event.target.value)); onValueChange(event.target.value); },
        onBlur: (event) => { onBlur && onBlur(event.target.value); },
        style: style
    });
};

const SelectInput = ({ value, onChange, options, style }) => {
    return createElement('select', {
        value: value || '',
        onChange: (e) => onChange(e.target.value),
        style: { ...style, cursor: 'pointer' }
    }, [
        createElement('option', { key: '__none', value: '' }, '— Select Phase —'),
        ...options.map(o => createElement('option', { key: o.code, value: o.code }, o.code + ' - ' + o.description))
    ]);
};

// Left section total width: inputs box ~336px + margin + labels ~140px + buttons ~30px ~= 560px
// Collapsed left: same 560px via minWidth
// This ensures week columns align between header, collapsed rows, and expanded rows
const LEFT_MIN_W = 590;

const BurnToolPhasedEquipment = () => {
    const trackApi = useApiTracker('burnToolEquipment');
    const updateUserDataArray = (data) => trackApi('updateUserDataArray', 'save', () => _updateUserDataArray(data)).catch(() => {});
    const deleteUserData = (params) => trackApi('deleteUserData', 'save', () => _deleteUserData(params)).catch(() => {});
    const loadUserData = (params) => trackApi('loadUserData', 'load', () => _loadUserData(params));
    const [units, setUnits] = useState({});
    const [unitDescriptions, setUnitDescriptions] = useState([]);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [currentBudgetDollars, setCurrentBudgetDollars] = useState(0);
    const [dayZeroDollars, setDayZeroDollars] = useState(0);
    const [coDollars, setCoDollars] = useState(0);
    const [actualDollars, setActualDollars] = useState(0);
    const [etcDollars, setEtcDollars] = useState(0);
    const [totalDollars, setTotalDollars] = useState(0);
    const [overUnderDollars, setOverUnderDollars] = useState(0);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [completionDateForecast] = useAtom(projectCompleteAtom);
    const [startDate] = useAtom(startDateActualAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const [userRole] = useAtom(userRoleAtom);
    const [previewRole] = useAtom(previewRoleAtom);
    const canSeeWipTabs = true;
    // Project Coordinators only get the Equipment Dashboard subtab — the forecast
    // grid never renders for them (see pcAllowedTabs in db/company.js).
    // Derived (not initial state) so it holds even when the role loads late.
    const isPC = effectiveIsProjectCoordinator(previewRole, userRole, userEmailCheck) || effectiveIsBuyer(previewRole, userRole, userEmailCheck);
    const [equipmentRates] = useAtom(equipmentRatesAtom);
    const [equipmentAssets] = useAtom(equipmentAssetsAtom);

    const [expandedWeeks, setExpandedWeeks] = useState(false);
    const [weekRangeLeft, setWeekRangeLeft] = useState(0);
    const [weekRangeRight, setWeekRangeRight] = useState(0);
    const [projectWeeks, setProjectWeeks] = useState([]);
    const [projectWeeksCloseToToday, setProjectWeeksCloseToToday] = useState([]);
    const [projectWeeksData, setProjectWeeksData] = useState({});
    const [projectDates, setProjectDates] = useState({});
    const [dollarsExpanded, setDollarsExpanded] = useState(false);
    const [totalsExpanded, setTotalsExpanded] = useState(false);
    const [dayZeroPhaseTotals, setDayZeroPhaseTotals] = useState({});
    const [coPhaseTotals, setCoPhaseTotals] = useState({});
    const [actualUnitTotals, setActualUnitTotals] = useState({});
    const [actualPhaseTotals, setActualPhaseTotals] = useState({});
    const [estGroupTotals, setEstGroupTotals] = useState({});
    const [actualUnitWeekDollars, setActualUnitWeekDollars] = useState({});
    const [versionInfo, setVersionInfo] = useState({});
    const containerRef = useRef(null);

    const [costUnitInfo, setCostUnitInfo] = useState({});
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [expandedGroupAct, setExpandedGroupAct] = useState({});
    const [userData, setUserData] = useState(false);
    const [customRows, setCustomRows] = useState([]);
    const [newRowName, setNewRowName] = useState('');
    const [phaseOptions, setPhaseOptions] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [hasResourceBudget, setHasResourceBudget] = useState(false);
    const [subTab, setSubTab] = useState('grid'); // 'grid' | 'dashboard'
    const activeSubTab = isPC ? 'dashboard' : subTab;

    // ── Derived totals ──
    useEffect(() => {
        setTotalDollars(fNum(pNum(actualDollars) + pNum(etcDollars), 1));
        setOverUnderDollars(fNum((pNum(actualDollars) + pNum(etcDollars)) - pNum(currentBudgetDollars), 1));
    }, [actualDollars, etcDollars, currentBudgetDollars]);

    // ── Load user data ──
    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
                setJobUserData(data?.data);
            });
        }
    }, [selectedJob, selectedVersion]);

    const reloadUserData = () => {
        loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
            setJobUserData(data?.data);
        });
    };

    // ── Scroll to center on range changes ──
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            const containerWidth = window.innerWidth;
            const scrollWidth = container.scrollWidth;
            if (scrollWidth > containerWidth) {
                // the page scrolls inside the shell's scroll area, not the window
                (document.querySelector('[data-scroll-area]') || window).scrollTo({ left: (scrollWidth - containerWidth) / 2, behavior: 'smooth' });
            }
        }
    }, [expandedWeeks, weekRangeLeft, weekRangeRight]);

    // ── Version info ──
    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion));
    }, [versions]);

    // ── Parse jobUserData into userData (keyed by normalized group name, full row spread) ──
    // Old data uses 'AssetName*week' format, new data uses 'GroupName=week'. Normalize both to '=' with stripped special chars.
    const normalizeName = (name) => (name + '').replace(/[^a-zA-Z0-9./\s\-^*=]/g, '');
    useEffect(() => {
        const uDataNew = {};
        const dzData = {};
        const coData = {};
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === TAB) {
                    const rawId = d?.Forecast_Row_ID || '';
                    let normalizedKey = rawId;
                    if (rawId.includes('*')) {
                        // Legacy format: "Asset Name*2026-02-16" → "Normalized Name=2026-02-16"
                        const [assetPart, weekPart] = rawId.split('*');
                        normalizedKey = normalizeName(assetPart) + '=' + weekPart;
                    } else if (rawId.includes('=')) {
                        // New format: keep as-is but normalize the name part
                        const [namePart, weekPart] = rawId.split('=');
                        normalizedKey = normalizeName(namePart) + '=' + weekPart;
                    } else {
                        // Header entry (no separator) — normalize name
                        normalizedKey = normalizeName(rawId);
                    }
                    // Store under normalized key; also preserve original data
                    if (!uDataNew[normalizedKey]) {
                        uDataNew[normalizedKey] = { id: normalizedKey, ...d };
                    } else {
                        uDataNew[normalizedKey] = { ...uDataNew[normalizedKey], ...d, id: normalizedKey };
                    }
                } else if (d?.Forecast_Tab === 'Day Zero Budget') {
                    dzData[d?.Forecast_Row_ID] = d;
                } else if (d?.Forecast_Tab === 'Change Order') {
                    coData[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label };
                }
            });

            // CO totals (52100)
            let totalCOCost = 0;
            let cOPhaseTotals = {};
            let coHasResource = false;
            Object.values(coData).forEach((row) => {
                if (row['status'] === 'Approved' || row['Status'] === 'Approved') {
                    totalCOCost += pNum(row[COST_TYPE]);
                    const phase = row['Cost_Code'] || '';
                    cOPhaseTotals[phase] = (cOPhaseTotals[phase] || 0) + pNum(row[COST_TYPE]);
                    if (pNum(row[COST_TYPE]) && row['Resource_Code']) coHasResource = true;
                }
            });

            // Day Zero totals (52100)
            let dayZeroTotal = 0;
            let dayZeroCostTotals = {};
            let foundResource = false;
            Object.values(dzData).forEach((row) => {
                if (row['Cost_Type'] == COST_TYPE) {
                    const val = pNum(row['Value_Amount']) || 0;
                    dayZeroTotal += val;
                    dayZeroCostTotals[row['Cost_Code']] = (dayZeroCostTotals[row['Cost_Code']] || 0) + val;
                    if (row['Resource_Code']) foundResource = true;
                }
            });

            // ETC total from saved week data (all keys normalized to '=' at parse time)
            let etcTotalCost = 0;
            Object.values(uDataNew).forEach((row) => {
                const id = row.id || '';
                const week = id.includes('=') ? id.split('=')[1] : null;
                if (week && row.Number_Hours && row.Rate && row.Number_Units &&
                    moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) &&
                    moment(week).isBefore(moment(completionDateForecast).endOf('week'))) {
                    etcTotalCost += pNum(row.Number_Hours) * pNum(row.Number_Units) * pNum(row.Rate);
                }
            });

            // Detect custom groups (explicit markers + orphaned weekly rows with no marker/actuals)
            const customGroupNames = [];
            const customGroupSet = new Set();
            jobUserData.forEach(d => {
                if (d?.Forecast_Tab === TAB && d?.Forecast_Field_Name === 'customGroup' && d.Forecast_Value) {
                    if (!customGroupSet.has(d.Forecast_Value)) {
                        customGroupSet.add(d.Forecast_Value);
                        customGroupNames.push(d.Forecast_Value);
                    }
                }
            });
            // Tiers that will render via groupedUnits (have actuals with a Unit_Number)
            const tiersWithActuals = new Set();
            (jobCostDetail || []).forEach(row => {
                if (row.Cost_Type_Code >= COST_TYPE_MIN && row.Cost_Type_Code <= COST_TYPE_MAX && row.Unit_Number && row.Unit_Number !== TPAY) {
                    const tierName = row.Equipment_Classification_Tier_Name_Description
                        ? normalizeName(row.Equipment_Classification_Tier_Name_Description)
                        : (row.Equipment_Classification_Tier_Name || '');
                    if (tierName) tiersWithActuals.add(tierName);
                }
            });
            // Orphan recovery: weekly rows (tier=week / tier*week) whose tier has no marker and no actuals
            jobUserData.forEach(d => {
                if (d?.Forecast_Tab !== TAB) return;
                if (d?.Forecast_Field_Name === 'customGroup') return;
                const rid = d?.Forecast_Row_ID || '';
                let tier = '';
                if (rid.includes('=')) tier = normalizeName(rid.split('=')[0]);
                else if (rid.includes('*')) tier = normalizeName(rid.split('*')[0]);
                if (!tier) return;
                if (customGroupSet.has(tier)) return;
                if (tiersWithActuals.has(tier)) return;
                customGroupSet.add(tier);
                customGroupNames.push(tier);
            });
            if (customGroupNames.length > 0) {
                setCustomRows(customGroupNames.map(name => ({
                    name, classificationDescription: '', units: [], weekActuals: {}, weekEtc: {}, isCustom: true
                })));
            }

            setDayZeroPhaseTotals(dayZeroCostTotals);
            setCoPhaseTotals(cOPhaseTotals);
            setHasResourceBudget(foundResource || coHasResource);
            setEtcDollars(fNum(etcTotalCost, 1));
            setDayZeroDollars(fNum(dayZeroTotal, 1));
            setCoDollars(fNum(totalCOCost, 1));
            setCurrentBudgetDollars(fNum(pNum(totalCOCost) + pNum(dayZeroTotal), 1));
            setUserData(uDataNew);
        }
    }, [jobUserData, jobCostDetail]);

    // ── Project weeks ──
    useEffect(() => {
        if (completionDateForecast) {
            let dateSections = {};
            let uniqueWeeks = {};
            let uniqueWeeksCloseToToday = {};
            // Anchor the week columns to the earliest of the actual start date
            // (set via the date-range picker / Set Dates) and the planned start,
            // so the grid begins where the range chip says and no early actuals/ETC get clipped.
            const startCandidates = [selectedJobData?.Planned_Start_Date, startDate].filter(Boolean).map(d => moment(d));
            const plannedStart = startCandidates.length ? moment.min(startCandidates) : moment('');
            const plannedFinish = moment(completionDateForecast);
            const fiveWeeksAgo = moment(versionInfo?.Version_Start_DateTime).add(-7, 'days').subtract(5, 'weeks').startOf('week');
            const fiveWeeksLater = moment(versionInfo?.Version_Start_DateTime).add(-7, 'days').add(10, 'weeks').startOf('week');
            const days = plannedFinish.diff(plannedStart, 'days');
            for (let i = 0; i < days; i++) {
                const day = plannedStart.clone().add(i, 'days');
                const currentWeek = day.clone().startOf('isoWeek');
                const wk = currentWeek.format('YYYY-MM-DD');
                if (currentWeek.isBetween(fiveWeeksAgo, fiveWeeksLater, 'week', '[]')) {
                    uniqueWeeksCloseToToday[wk] = 1;
                }
                uniqueWeeks[wk] = 1;
                dateSections[day.format('YYYY-MM-DD')] = wk;
            }
            setProjectDates(dateSections);
            setProjectWeeks(Object.keys(uniqueWeeks));
            setProjectWeeksCloseToToday(Object.keys(uniqueWeeksCloseToToday));
        }
    }, [selectedJobData, versionInfo, completionDateForecast, startDate]);

    // ── Parse cost detail for equipment rows ──
    useEffect(() => {
        const unitGroups = {};
        let totalCost = 0;
        const phaseSet = {};
        const phaseActuals = {};
        jobCostDetail?.forEach((row) => {
            if (row.Cost_Type_Code >= COST_TYPE_MIN && row.Cost_Type_Code <= COST_TYPE_MAX && row.Unit_Number !== TPAY) {
                totalCost += pNum(row.Job_Cost);
                const actualPhase = row.Phase_Code || '';
                phaseActuals[actualPhase] = (phaseActuals[actualPhase] || 0) + pNum(row.Job_Cost);
                if (!unitGroups[row.Unit_Number] && row.Unit_Number) {
                    unitGroups[row.Unit_Number] = [row];
                } else if (row.Unit_Number) {
                    unitGroups[row.Unit_Number].push(row);
                }
                if (row.Phase_Code) {
                    phaseSet[row.Phase_Code] = row.Phase_Code_Description || phaseCodes[row.Phase_Code] || '';
                }
            }
        });

        const unitInfo = {};
        Object.keys(unitGroups).forEach((u) => {
            const firstRow = unitGroups[u][0];
            const tierName = firstRow.Equipment_Classification_Tier_Name || '';
            const tierNameDesc = firstRow.Equipment_Classification_Tier_Name_Description
                ? (firstRow.Equipment_Classification_Tier_Name_Description + '').replace(/[^a-zA-Z0-9./\s\-^*=]/g, '')
                : '';
            unitInfo[u] = {
                description: firstRow.Remark_Description || '',
                classification: tierNameDesc || tierName,
                classificationDescription: tierNameDesc || tierName,
                code: u
            };
        });

        setActualDollars(fNum(totalCost, 1));
        setActualPhaseTotals(phaseActuals);
        setUnits(unitGroups);
        setCostUnitInfo(unitInfo);
    }, [jobCostDetail, phaseCodes]);

    // Build phase options from Day Zero Budget + Change Orders only (compatible phases)
    useEffect(() => {
        const compatiblePhases = {};
        Object.keys(dayZeroPhaseTotals).forEach(p => { if (p) compatiblePhases[p] = phaseCodes?.[p] || ''; });
        Object.keys(coPhaseTotals).forEach(p => { if (p && !compatiblePhases[p]) compatiblePhases[p] = phaseCodes?.[p] || ''; });
        const opts = Object.keys(compatiblePhases)
            .filter(c => c !== '1000' && c !== '990000')
            .sort((a, b) => (a + '').localeCompare(b + '', undefined, { numeric: true, sensitivity: 'base' }))
            .map(code => ({ code, description: compatiblePhases[code] || '' }));
        setPhaseOptions(opts);
    }, [dayZeroPhaseTotals, coPhaseTotals, phaseCodes]);

    useEffect(() => {
        setUnitDescriptions(Object.values(costUnitInfo).sort((a, b) => a.code.localeCompare(b.code)));
    }, [costUnitInfo]);

    useEffect(() => {
        // Run whenever job data is loaded — not only when units are detected. Jobs with only
        // user-added (custom, 0-unit) rows have no unitDescriptions, but their per-week ETC still
        // needs to populate projectWeeksData so the top Estimate $ row totals show.
        if (jobUserData) calculateTotals();
    }, [userData, projectWeeks, unitDescriptions, jobCostDetail, projectDates, versionInfo, completionDateForecast]);

    // ── Calculate totals ──
    const calculateTotals = () => {
        let etcDollars = 0;
        let projectData = {};
        let unitActualDollars = {};
        let unitTotalDollars = {};

        jobCostDetail && jobCostDetail.forEach((row) => {
            const unit = row.Unit_Number;
            const date = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM-DD');
            const week = projectDates[date];
            if (row.Cost_Type_Code >= COST_TYPE_MIN && row.Cost_Type_Code <= COST_TYPE_MAX && unit !== TPAY) {
                if (unitActualDollars[unit]?.[week]) {
                    unitActualDollars[unit][week] += pNum(row.Job_Cost);
                } else if (unitActualDollars[unit]) {
                    unitActualDollars[unit][week] = pNum(row.Job_Cost);
                } else {
                    unitActualDollars[unit] = { [week]: pNum(row.Job_Cost) };
                }
                unitTotalDollars[unit] = (unitTotalDollars[unit] || 0) + pNum(row.Job_Cost);
            }
        });

        // Per-week ETC from saved group-level data
        const unitCodes = new Set(unitDescriptions.map(u => u.code));
        const groupEst = {};
        projectWeeks && projectWeeks.forEach((week) => {
            let etcWeek = 0;
            let actWeek = 0;

            // Group-level ETC (keys where left side of = is NOT a unit code)
            unitDescriptions.forEach((unit) => {
                actWeek += (unitActualDollars[unit.code]?.[week] || 0);
            });

            projectData[week] = { actualDollars: actWeek ? fNum(actWeek, 1) : '', etcDollars: '' };
        });

        // Scan all userData for group-level ETC entries (all keys normalized to '=' at parse time)
        const versionWeekStart = moment(versionInfo?.Version_Start_DateTime).startOf('week');
        userData && Object.keys(userData).forEach(key => {
            const parts = key.split('=');
            if (parts.length === 2) {
                const rowKey = parts[0];
                const week = parts[1];
                if (week && moment(week).isValid()) {
                    const w = userData[key];
                    if (!w || !moment(week).isBefore(moment(completionDateForecast))) return;
                    const val = pNum(w.Number_Hours) * pNum(w.Rate) * pNum(w.Number_Units);
                    if (!val) return;
                    // Display per-week estimate for every valid week (including historical)
                    if (projectData[week]) {
                        projectData[week].etcDollars = fNum(pNum(projectData[week].etcDollars) + val, 1);
                    }
                    // Only future weeks contribute to the forward-looking ETC total and group sums
                    if (moment(week).isAfter(versionWeekStart)) {
                        etcDollars += val;
                        groupEst[rowKey] = (groupEst[rowKey] || 0) + val;
                    }
                }
            }
        });

        setActualUnitTotals(unitTotalDollars);
        setActualUnitWeekDollars(unitActualDollars);
        setEstGroupTotals(groupEst);
        setProjectWeeksData(projectData);
        setEtcDollars(fNum(etcDollars, 1));
    };

    // ── GroupRow component ──
    const GroupRow = ({ group, gIdx }) => {
        const rowId = group.name;
        const isGroupCollapsed = collapsedGroups[group.name];
        const isActExpanded = expandedGroupAct[group.name];
        const groupUnitCount = group.units.length;
        const groupActualTotal = group.units.reduce((sum, u) => sum + (actualUnitTotals[u.code] || 0), 0);
        // Sum the per-week ETC already computed for this row (group.weekEtc) instead of
        // estGroupTotals, which only populates when calculateTotals runs (i.e. the job has
        // detected equipment units). Custom/zero-unit rows would otherwise show ETC 0 even
        // with weekly estimates. This keeps the card total in sync with the weekly cells.
        const groupEstTotal = Object.values(group.weekEtc || {}).reduce((sum, v) => sum + pNum(v), 0);
        const groupTotal = groupActualTotal + groupEstTotal;

        const savedData = userData[rowId];
        const [data, setData] = useState(savedData);
        const [dataRows, setDataRows] = useState({});
        // Per-week values typed into the cells but not yet in dataRows. Kept in a ref so
        // typing never re-renders the row; merged over dataRows wherever a week is read,
        // so a re-render or a spread doesn't fall back to the pre-edit values.
        const weekEdits = useRef({});
        const weekRow = (rows, key) => (weekEdits.current[key] ? { ...(rows[key] || {}), ...weekEdits.current[key] } : rows[key]);
        const [expandEst, setExpandEst] = useState(true);
        const [groupStartDate, setGroupStartDate] = useState(savedData?.Phase_Start ? moment(savedData.Phase_Start).add(6, 'hours').format('YYYY-MM-DD') : '');
        const [groupEndDate, setGroupEndDate] = useState(savedData?.Phase_End ? moment(savedData.Phase_End).add(6, 'hours').format('YYYY-MM-DD') : '');
        const [burnRateTopEst, setBurnRateEst] = useState(savedData?.Rate || '');
        const [hrsPerWeekEst, setHrsPerWeekEst] = useState(savedData?.Number_Hours || 0);
        const [crewSizeEst, setCrewSizeEst] = useState(savedData?.Number_Units || '');
        const [selectedPhase, setSelectedPhase] = useState(savedData?.Cost_Code || '100102');

        const groupBudget = (dayZeroPhaseTotals[selectedPhase] || 0) + (coPhaseTotals[selectedPhase] || 0);
        const groupOverUnder = groupTotal - groupBudget;

        useEffect(() => {
            const userLocalData = {};
            userData && Object.keys(userData).forEach((key) => {
                if (key.startsWith(rowId + '=') || key === rowId) userLocalData[key] = userData[key];
            });
            setDataRows(userLocalData);
            weekEdits.current = {};
            // Sync header fields from loaded userData (useState only captures initial value)
            const sd = userData && userData[rowId];
            if (sd) {
                setData(sd);
                if (sd.Phase_Start) setGroupStartDate(moment(sd.Phase_Start).add(6, 'hours').format('YYYY-MM-DD'));
                if (sd.Phase_End) setGroupEndDate(moment(sd.Phase_End).add(6, 'hours').format('YYYY-MM-DD'));
                if (sd.Rate) setBurnRateEst(sd.Rate);
                if (sd.Number_Hours) setHrsPerWeekEst(sd.Number_Hours);
                if (sd.Number_Units) setCrewSizeEst(sd.Number_Units);
                if (sd.Cost_Code) setSelectedPhase(sd.Cost_Code);
                if (sd.expandEst !== undefined) setExpandEst(sd.expandEst);
            }
        }, [userData]);

        const debounce = (func, delay) => {
            let timeoutId;
            return (...args) => { if (timeoutId) clearTimeout(timeoutId); timeoutId = setTimeout(() => func(...args), delay); };
        };

        const saveValue = (burnRate, hoursWeek, crewSize, start, end, phase) => {
            const bRate = burnRate ?? burnRateTopEst;
            const hWeek = hoursWeek ?? hrsPerWeekEst;
            const cSize = crewSize ?? crewSizeEst;
            const sDate = start ?? groupStartDate;
            const eDate = end ?? groupEndDate;
            const ph = phase ?? selectedPhase;

            // Header + every touched week go up as ONE request. A request per week ran
            // them concurrently and SQL picked some as deadlock victims, silently
            // dropping those weeks.
            const rows = [];
            rows.push({
                userId: userId || '154045',
                userName: userName || 'Seth Bailey',
                fieldName: 'row',
                tab: TAB,
                rowId: rowId,
                version: selectedVersion + '',
                value: bRate && hWeek && cSize ? pNum(bRate) * pNum(hWeek) * pNum(cSize) : '',
                valueAmount: bRate && hWeek && cSize ? pNum(bRate) * pNum(hWeek) * pNum(cSize) : '',
                id: selectedJob,
                costCode: ph,
                costType: COST_TYPE,
                lockedVersion: lockedVersion,
                projectStart: sDate,
                projectEnd: completionDateForecast,
                phaseStart: sDate,
                phaseEnd: eDate,
                rate: bRate,
                numberHours: hWeek,
                numberUnits: cSize
            });

            // Compute unique weeks in the date range
            let uniqueWeeks = {};
            if (sDate && eDate) {
                const plannedStart = moment(sDate);
                const plannedFinish = moment(eDate);
                const days = plannedFinish.diff(plannedStart, 'days');
                const lastWeekBeforeForecast = moment(versionInfo?.Version_Start_DateTime).add(-7, 'days').startOf('week');
                for (let i = 0; i <= days; i++) {
                    const wk = plannedStart.clone().add(i, 'days').startOf('isoWeek');
                    if (wk.isAfter(lastWeekBeforeForecast, 'week')) uniqueWeeks[wk.format('YYYY-MM-DD')] = 1;
                }
            }

            let uData = JSON.parse(JSON.stringify(dataRows));
            uData[rowId] = { burnRateTopEst: bRate, hrsPerWeekEst: hWeek, crewSizeEst: cSize, startDate: sDate, endDate: eDate, expandEst: expandEst };

            if (cSize && bRate && hWeek && sDate && eDate) {
                Object.keys(uniqueWeeks).forEach((week) => {
                    const cur = weekRow(uData, rowId + '=' + week) || {};
                    const isZero = (!cur.Rate || Number(cur.Rate) === 0) && (!cur.Number_Hours || Number(cur.Number_Hours) === 0) && (!cur.Number_Units || Number(cur.Number_Units) === 0);
                    if (isZero) {
                        rows.push({
                            active: 1, userId: userId || '154045', userName: userName || 'Seth Bailey',
                            fieldName: 'row', tab: TAB, rowId: rowId + '=' + week, version: selectedVersion + '',
                            value: bRate, id: selectedJob, costCode: ph, costType: COST_TYPE, lockedVersion: lockedVersion,
                            projectStart: sDate, projectEnd: completionDateForecast, phaseStart: sDate, phaseEnd: eDate,
                            rate: bRate + '', numberHours: hWeek + '', numberUnits: cSize + ''
                        });
                        uData[rowId + '=' + week] = { Rate: bRate + '', Number_Hours: hWeek + '', Number_Units: cSize + '' };
                    }
                });
            }

            // Clear weeks outside the date range
            projectWeeks.forEach((week) => {
                if (!uniqueWeeks[week] && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).add(-1, 'week'), 'week')) {
                    const cur = weekRow(uData, rowId + '=' + week);
                    delete weekEdits.current[rowId + '=' + week];
                    if (cur?.Number_Hours || cur?.Rate || cur?.Number_Units) {
                        rows.push({
                            userId: userId || '154045', userName: userName || 'Seth Bailey',
                            fieldName: 'row', tab: TAB, rowId: rowId + '=' + week, version: selectedVersion + '',
                            value: '', id: selectedJob, costCode: ph, costType: COST_TYPE, lockedVersion: lockedVersion,
                            projectStart: sDate, projectEnd: completionDateForecast, phaseStart: sDate, phaseEnd: eDate,
                            rate: 0, numberHours: 0, numberUnits: 0
                        });
                    }
                    uData[rowId + '=' + week] = { Rate: '', Number_Hours: '', Number_Units: '' };
                }
            });
            updateUserDataArray(rows);

            if (cSize && bRate && hWeek && sDate && eDate) { setData(uData[rowId]); setDataRows(uData); }
        };

        const setDefaultsAcrossWeeks = (field) => {
            if (!groupStartDate || !groupEndDate) return;
            let uniqueWeeks = {};
            const days = moment(groupEndDate).diff(moment(groupStartDate), 'days');
            const lastWeekBefore = moment(versionInfo?.Version_Start_DateTime).add(-7, 'days').startOf('week');
            for (let i = 0; i <= days; i++) {
                const wk = moment(groupStartDate).clone().add(i, 'days').startOf('isoWeek');
                if (wk.isAfter(lastWeekBefore, 'week')) uniqueWeeks[wk.format('YYYY-MM-DD')] = 1;
            }
            let uData = JSON.parse(JSON.stringify(dataRows));
            // One request for the whole spread (see saveValue).
            const rows = [];
            Object.keys(uniqueWeeks).forEach(week => {
                const wk = rowId + '=' + week;
                const prev = weekRow(uData, wk) || {};
                // The applied field takes the group value; the other two keep the week's own
                // saved value, only falling back to the group estimate when genuinely unset.
                const rateV = field === 'rate' ? burnRateTopEst : (prev.Rate !== undefined && prev.Rate !== '' ? prev.Rate : burnRateTopEst);
                const hrsV = field === 'hrs' ? hrsPerWeekEst : (prev.Number_Hours !== undefined && prev.Number_Hours !== '' ? prev.Number_Hours : hrsPerWeekEst);
                const unitsV = field === 'pieces' ? crewSizeEst : (prev.Number_Units !== undefined && prev.Number_Units !== '' ? prev.Number_Units : crewSizeEst);
                // Never persist a half-written row. Applying Hrs/Pieces while the group Rate is
                // blank used to write rate-0 weeks (hours/units set) that the summary then
                // resurrected via the asset-header rate fallback (the phantom 3,722). Require
                // all three present, and store the computed cost — not a single field value.
                if (!pNum(rateV) || !pNum(hrsV) || !pNum(unitsV)) return;
                const computed = pNum(rateV) * pNum(hrsV) * pNum(unitsV);
                rows.push({
                    active: 1, userId: userId || '154045', userName: userName || 'Seth Bailey',
                    fieldName: 'row', tab: TAB, rowId: wk, version: selectedVersion + '',
                    value: computed, valueAmount: computed,
                    id: selectedJob, costCode: selectedPhase, costType: COST_TYPE, lockedVersion: lockedVersion,
                    numberUnits: unitsV, numberHours: hrsV, rate: rateV
                });
                uData[wk] = { Rate: rateV, Number_Hours: hrsV, Number_Units: unitsV };
                delete weekEdits.current[wk];
            });
            if (rows.length) updateUserDataArray(rows);
            setData(uData[rowId]);
            setDataRows(uData);
        };

        const handleDebouncedChange = debounce((bRate, hWeek, cSize, start, end) => {
            saveValue(bRate, hWeek, cSize, start, end);
        }, 500);

        const handlePhaseChange = (phaseCode) => {
            setSelectedPhase(phaseCode);
            saveValue(null, null, null, null, null, phaseCode);
        };

        // ── WeekValue (per-week cell for this row) ──
        const WeekValue = (props) => {
            const [user] = useState(props.data);
            const week = props.week;
            const [hoursWeek, setHoursWeek] = useState(user?.Number_Hours ? f(user.Number_Hours) : '');
            const [crewSize, setCrewSize] = useState(user?.Number_Units ? f(pNum(user.Number_Units)) : '');
            const [burnRateEst, setBurnRateEstW] = useState(user?.Rate ? f(pNum(user.Rate)) : '');
            const [estWeekCost, setEstWeekCost] = useState('');
            const beforeToday = moment(versionInfo?.Version_Start_DateTime).isAfter(moment(week).add(7, 'days'));
            const isThisWeek = moment(week).add(7, 'days').isSame(moment(versionInfo?.Version_Start_DateTime), 'week');
            const isBeforeThisWeek = moment(week).isBefore(moment(versionInfo?.Version_Start_DateTime), 'week');
            const isBetweenInterval = groupStartDate && groupEndDate ? moment(week).isBetween(moment(groupStartDate).startOf('isoWeek').add(-1, 'day'), moment(groupEndDate).add(1, 'day')) : false;
            const changeListRefWeek = useRef([]);
            const groupActWeek = group.weekActuals[week] || 0;

            useEffect(() => {
                if (hoursWeek?.floatValue && crewSize?.floatValue && burnRateEst?.floatValue) {
                    setEstWeekCost(fNum(burnRateEst.floatValue * hoursWeek.floatValue * crewSize.floatValue, 1));
                } else {
                    setEstWeekCost('');
                }
            }, [hoursWeek, crewSize, burnRateEst]);

            const debounceSaveWeek = useCallback(debounce(() => {
                const uniqueRows = {};
                changeListRefWeek.current.forEach(v => { uniqueRows[v.row] = v; });
                saveValuesWeek(Object.values(uniqueRows));
                changeListRefWeek.current = [];
            }, 2000), []);

            const handleInputChangeWeek = (v) => {
                // Blank fields are left out — saveValuesWeek keeps the prior saved value for those.
                const edit = {};
                [['Rate', v.rate], ['Number_Hours', v.hoursWeek], ['Number_Units', v.crewSize]].forEach(([k, x]) => {
                    if (x !== undefined && x !== null && x !== '') edit[k] = pNum(x);
                });
                weekEdits.current[v.row] = edit;
                changeListRefWeek.current.push(v);
                debounceSaveWeek();
            };

            const saveValuesWeek = (values) => {
                // Only fall back to the prior saved value when the field was not edited
                // (null/undefined/empty) — NOT when the user explicitly typed 0.
                const pick = (entered, prior) => (entered === undefined || entered === null || entered === '') ? prior : entered;
                values.forEach((v) => {
                    const numOfWorkers = pick(v.crewSize, user?.Number_Units) || 0;
                    const hoursPerWeek = pick(v.hoursWeek, user?.Number_Hours) || 0;
                    const burnPerHour = pick(v.rate, user?.Rate) || 0;
                    updateUserDataArray({
                        active: 1, userId: userId || '154045', userName: userName || 'Seth Bailey',
                        fieldName: 'row', tab: TAB, rowId: v.row,
                        costCode: selectedPhase, version: selectedVersion + '',
                        value: pNum(numOfWorkers) * pNum(hoursPerWeek) * pNum(burnPerHour),
                        valueAmount: pNum(numOfWorkers) * pNum(hoursPerWeek) * pNum(burnPerHour),
                        projectStart: selectedJobData?.Planned_Start_Date,
                        projectFinish: completionDateForecast || selectedJobData?.Planned_Complete_Date,
                        phaseStart: groupStartDate, phaseFinish: groupEndDate,
                        rate: burnPerHour, numberHours: hoursPerWeek, numberUnits: numOfWorkers,
                        hours: pNum(numOfWorkers) * pNum(hoursPerWeek),
                        active: 1, id: selectedJob, costType: COST_TYPE, lockedVersion: lockedVersion
                    });
                });
            };

            const setDefaults = () => {
                updateUserDataArray({
                    active: 1, userId: userId || '154045', userName: userName || 'Seth Bailey',
                    fieldName: 'row', tab: TAB, rowId: rowId + '=' + week, costCode: selectedPhase,
                    version: selectedVersion + '',
                    value: pNum(crewSizeEst) * pNum(hrsPerWeekEst) * pNum(burnRateTopEst),
                    valueAmount: pNum(crewSizeEst) * pNum(hrsPerWeekEst) * pNum(burnRateTopEst),
                    projectStart: selectedJobData?.Planned_Start_Date,
                    projectFinish: completionDateForecast || selectedJobData?.Planned_Complete_Date,
                    phaseStart: groupStartDate, phaseFinish: groupEndDate,
                    rate: burnRateTopEst, numberHours: hrsPerWeekEst, numberUnits: crewSizeEst,
                    hours: pNum(crewSizeEst) * pNum(hrsPerWeekEst),
                    active: 1, id: selectedJob, costType: COST_TYPE, lockedVersion: lockedVersion
                });
                // Push the defaults into the row's data so the cell re-seeds from them
                // (the week key changes). Setting the cell's own state to bare strings
                // blanked the three inputs.
                delete weekEdits.current[rowId + '=' + week];
                setDataRows(prev => ({ ...prev, [rowId + '=' + week]: { ...(prev[rowId + '=' + week] || {}), Rate: burnRateTopEst, Number_Hours: hrsPerWeekEst, Number_Units: crewSizeEst } }));
            };

            return (
                <View style={{ borderRight: isThisWeek ? '2px solid #1e40af' : 'none' }}>
                    <Text style={[s.rateC, weekDateHeader]}>{week}</Text>
                    <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{estWeekCost}</Text>
                    {expandEst ? <View>
                        {!beforeToday && isBetweenInterval ? (
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }} />
                                <Pressable tabIndex={4} style={{ borderLeft: '10px solid transparent', borderRight: '10px solid #94a3b8', borderBottom: '10px solid transparent', right: 0, top: 0, position: 'absolute' }} onPress={setDefaults} />
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator decimalSeparator="." tabIndex={1} valueIsNumericString={true}
                                    value={fNum(burnRateEst?.formattedValue)}
                                    onValueChange={(t) => { setBurnRateEstW(t); handleInputChangeWeek({ rate: t.floatValue, hoursWeek: hoursWeek?.formattedValue, crewSize: crewSize?.formattedValue, key: 'burnRateEst', row: rowId + '=' + week }); }}
                                />
                            </View>
                        ) : <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{fNum(pNum(burnRateEst?.formattedValue))}</Text>}
                        {!beforeToday && isBetweenInterval ? (
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }} />
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator decimalSeparator="." tabIndex={3} valueIsNumericString={true}
                                    value={fNum(hoursWeek?.formattedValue, 1)}
                                    onValueChange={(t) => { setHoursWeek(t); handleInputChangeWeek({ rate: burnRateEst?.formattedValue, hoursWeek: t.floatValue, crewSize: crewSize?.formattedValue, key: 'hoursWeek', row: rowId + '=' + week }); }}
                                />
                            </View>
                        ) : <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{hoursWeek?.formattedValue}</Text>}
                        {!beforeToday && isBetweenInterval ? (
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }} />
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator decimalSeparator="." tabIndex={2} valueIsNumericString={true}
                                    value={fNum(crewSize?.formattedValue)}
                                    onValueChange={(t) => { setCrewSize(t); handleInputChangeWeek({ rate: burnRateEst?.formattedValue, hoursWeek: hoursWeek?.formattedValue, crewSize: t.floatValue, key: 'crewSize', row: rowId + '=' + week }); }}
                                />
                            </View>
                        ) : <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{fNum(pNum(crewSize?.formattedValue))}</Text>}
                    </View> : null}
                    <Text style={[s.rate, { backgroundColor: groupActWeek > 0 ? '#f9fafb' : 'white', color: groupActWeek > 0 ? '#475569' : '#94a3b8', fontWeight: groupActWeek > 0 ? '600' : 'normal', borderTopWidth: 2, borderTopColor: '#1e40af' }]}>
                        {groupActWeek > 0 ? fNum(groupActWeek, 1) : ''}
                    </Text>
                    {isActExpanded && group.units.map((unit) => {
                        const unitAct = actualUnitWeekDollars[unit.code]?.[week] || 0;
                        return <Text key={unit.code} style={[s.rate, { fontSize: 10, backgroundColor: unitAct > 0 ? '#f9fafb' : 'transparent', color: unitAct > 0 ? '#475569' : '#94a3b8', fontWeight: unitAct > 0 ? '600' : 'normal' }]}>{unitAct > 0 ? fNum(unitAct, 1) : ''}</Text>;
                    })}
                </View>
            );
        };

        // ── Collapsed view ──
        if (isGroupCollapsed) {
            return (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: gIdx > 0 ? 4 : 0 }}>
                    <Pressable
                        onPress={() => toggleGroupCollapse(group.name)}
                        style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 8, backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', position: 'sticky', left: 0, zIndex: 5, minWidth: LEFT_MIN_W }}
                    >
                        <Icon3 name="MdChevronRight" size={18} color="#475569" />
                        <EquipmentIcon description={group.name} size={14} />
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e293b', marginLeft: 4 }}>{group.name}</Text>
                        {group.classificationDescription && group.classificationDescription !== group.name ? <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>{group.classificationDescription}</Text> : null}
                        <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 8 }}>({groupUnitCount} unit{groupUnitCount !== 1 ? 's' : ''})</Text>
                        {selectedPhase ? <Text style={{ fontSize: 10, color: '#1e40af', marginLeft: 8 }}>Phase: {selectedPhase}</Text> : null}
                        {group.isCustom ? <Icon3 onPress={(e) => { e.stopPropagation && e.stopPropagation(); setConfirmDelete(group.name); }} name="MdClose" size={14} color="#ef4444" style={{ marginLeft: 8, cursor: 'pointer' }} /> : null}
                    </Pressable>
                    {activeWeeks.map((week, wIdx) => {
                        const wEtc = group.weekEtc[week] || 0;
                        const wAct = group.weekActuals[week] || 0;
                        const wTotal = wEtc + wAct;
                        return (
                            <View key={wIdx} style={{ borderRight: forecastWeek.isSame(moment(week), 'week') ? '2px solid #1e40af' : 'none' }}>
                                <Text style={[s.rateC, weekDateHeader, { color: 'transparent', height: 0, overflow: 'hidden', padding: 0, margin: 0, lineHeight: 0, fontSize: 0 }]}>{week}</Text>
                                <Text style={[s.rate, { backgroundColor: wTotal > 0 ? '#f8fafc' : 'transparent', color: '#475569', fontWeight: wTotal > 0 ? '600' : 'normal', fontSize: 10 }]}>{wTotal > 0 ? fNum(wTotal, 1) : ''}</Text>
                            </View>
                        );
                    })}
                </View>
            );
        }

        // ── Expanded view ──
        return (
            <View style={{ marginBottom: 4 }}>
                <View style={{ backgroundColor: 'white', flexDirection: 'row', marginTop: gIdx > 0 ? 4 : 0, marginBottom: 2 }}>
                    <View style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 5, flexDirection: 'row', minWidth: LEFT_MIN_W }}>
                        <View>
                            <Pressable
                                onPress={() => toggleGroupCollapse(group.name)}
                                style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, backgroundColor: 'white', borderRadius: 4 }}
                            >
                                <View style={{ flexDirection: 'row', alignItems: 'center', width: 18, justifyContent: 'center' }}>
                                    <Icon3 name="MdExpandMore" size={14} color="#475569" />
                                </View>
                                <EquipmentIcon description={group.name} size={14} />
                                <Text style={{ fontSize: 11, fontWeight: '700', color: '#1e293b', marginLeft: 2 }}>{group.name}</Text>
                                {group.classificationDescription && group.classificationDescription !== group.name ? <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>{group.classificationDescription}</Text> : null}
                                <Text style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>({groupUnitCount})</Text>
                                <Icon3 onPress={(e) => { e.stopPropagation && e.stopPropagation(); reloadUserData(); }} name="MdSync" size={12} color="#64748b" style={{ marginLeft: 6, cursor: 'pointer' }} />
                                {group.isCustom ? <Icon3 onPress={(e) => { e.stopPropagation && e.stopPropagation(); setConfirmDelete(group.name); }} name="MdClose" size={14} color="#ef4444" style={{ marginLeft: 4, cursor: 'pointer' }} /> : null}
                            </Pressable>
                            {/* Phase dropdown */}
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2, paddingLeft: 18 }}>
                                <Text style={{ fontSize: 9, fontWeight: '600', color: '#64748b', marginRight: 4 }}>Phase:</Text>
                                <SelectInput
                                    value={selectedPhase}
                                    onChange={handlePhaseChange}
                                    options={phaseOptions}
                                    style={{ height: 22, fontSize: 10, border: '1px solid #e2e5ea', borderRadius: 3, maxWidth: 280 }}
                                />
                            </View>
                            {/* Inputs row */}
                            <View style={{ zIndex: 20, backgroundColor: 'white', borderRadius: 4, border: '1px solid #e2e8f0', padding: 0, paddingLeft: 5, overflow: 'hidden' }}>
                                <View style={{ flexDirection: 'row', alignItems: 'stretch', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    {[
                                        { label: 'Start Date', type: 'date', width: 100, value: groupStartDate, onChange: (t) => { setGroupStartDate(t); saveValue(null, null, null, moment(t).format('YYYY-MM-DD'), null); }, onBlur: (t) => { saveValue(null, null, null, moment(t).format('YYYY-MM-DD'), null); } },
                                        { label: 'End Date', type: 'date', width: 100, value: groupEndDate, onChange: (t) => { setGroupEndDate(t); saveValue(null, null, null, null, moment(t).format('YYYY-MM-DD')); }, onBlur: (t) => { saveValue(null, null, null, null, moment(t).format('YYYY-MM-DD')); }, warn: groupEndDate && moment(groupEndDate).isAfter(completionDateForecast) },
                                        { label: 'Rate', type: 'text', width: 52, value: burnRateTopEst, onChange: (v) => { setBurnRateEst(v); handleDebouncedChange(v, hrsPerWeekEst, crewSizeEst, groupStartDate, groupEndDate); }, onApply: () => setDefaultsAcrossWeeks('rate') },
                                        { label: 'Hrs/Wk', type: 'text', width: 44, value: hrsPerWeekEst, onChange: (v) => { setHrsPerWeekEst(v); handleDebouncedChange(burnRateTopEst, v, crewSizeEst, groupStartDate, groupEndDate); }, onApply: () => setDefaultsAcrossWeeks('hrs') },
                                        { label: 'Pieces', type: 'text', width: 40, value: crewSizeEst, onChange: (v) => { setCrewSizeEst(v); handleDebouncedChange(burnRateTopEst, hrsPerWeekEst, v, groupStartDate, groupEndDate); }, onApply: () => setDefaultsAcrossWeeks('pieces') },
                                    ].map((field, idx) => (
                                        <View key={idx} style={{ width: field.width, borderRight: idx < 4 ? '1px solid #e2e8f0' : 'none' }}>
                                            <Text style={{ fontSize: 8, fontWeight: '600', color: '#64748b', textAlign: 'center', paddingVertical: 2, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: 0.3 }}>{field.label}</Text>
                                            <View style={{ padding: 2, position: 'relative' }}>
                                                {field.onApply && <Pressable style={{ position: 'absolute', right: 1, top: 1, zIndex: 2, width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid #94a3b8', borderBottom: '6px solid transparent' }} onPress={field.onApply} />}
                                                {field.type === 'date' ? (
                                                    <DateInput value={field.value} onValueChange={field.onChange} onBlur={field.onBlur} style={{ width: '100%', height: 22, fontSize: 10, border: '1px solid #e2e5ea', borderRadius: 3, paddingLeft: 2, textAlign: 'center', color: field.warn ? '#dc2626' : '#1f2937' }} />
                                                ) : (
                                                    <TextInput style={{ width: '100%', height: 22, fontSize: 10, border: '1px solid #e2e5ea', borderRadius: 3, paddingLeft: 2, textAlign: 'center', color: '#1f2937' }} onChangeText={field.onChange} value={field.value + ''} />
                                                )}
                                            </View>
                                        </View>
                                    ))}
                                </View>
                                {/* Summary table */}
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9' }}>
                                    <Text style={{ width: 52, fontSize: 10, fontWeight: '600', color: '#64748b', padding: 2, textAlign: 'right' }}></Text>
                                    {(hasResourceBudget ? ['Budget', 'Actual\nCosts', 'Est. To\nCompl.', 'Est. At\nCompl.', 'Over/\nUnder'] : ['Actual\nCosts', 'Est. To\nCompl.', 'Est. At\nCompl.']).map((h, i) => (
                                        <Text key={i} style={{ width: 62, fontSize: 10, fontWeight: '600', color: '#64748b', padding: 2, textAlign: 'right', borderLeft: '1px solid #e2e8f0' }}>{h}</Text>
                                    ))}
                                </View>
                                <View style={{ flexDirection: 'row', backgroundColor: 'white', borderTop: '1px solid #f0f0f0' }}>
                                    <Text style={{ width: 52, fontSize: 11, fontWeight: '600', color: '#475569', padding: 2, textAlign: 'right', backgroundColor: '#f8fafc' }}>Dollars</Text>
                                    {hasResourceBudget && <Text style={{ width: 62, fontSize: 11, color: '#1f2937', padding: 2, textAlign: 'right', borderLeft: '1px solid #f0f0f0' }}>{fNum(groupBudget, 1)}</Text>}
                                    <Text style={{ width: 62, fontSize: 11, color: '#1f2937', padding: 2, textAlign: 'right', borderLeft: '1px solid #f0f0f0' }}>{fNum(groupActualTotal, 1)}</Text>
                                    <Text style={{ width: 62, fontSize: 11, color: '#1f2937', padding: 2, textAlign: 'right', borderLeft: '1px solid #f0f0f0' }}>{fNum(groupEstTotal, 1)}</Text>
                                    <Text style={{ width: 62, fontSize: 11, color: '#1f2937', padding: 2, textAlign: 'right', borderLeft: '1px solid #f0f0f0' }}>{fNum(groupTotal, 1)}</Text>
                                    {hasResourceBudget && <Text style={{ width: 62, fontSize: 11, color: groupOverUnder > 0 ? '#dc2626' : '#1f2937', fontWeight: groupOverUnder > 0 ? '600' : undefined, padding: 2, textAlign: 'right', borderLeft: '1px solid #f0f0f0' }}>{fNum(groupOverUnder, 1)}</Text>}
                                </View>
                            </View>
                        </View>
                        {/* Row labels column */}
                        <View style={{ marginLeft: totalsExpanded ? 188 : 60 }}>
                            <Text style={s.spanSC}></Text>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Icon3 style={{ cursor: 'pointer' }} onPress={() => setExpandEst(!expandEst)} name={expandEst ? "MdExpandMore" : "MdChevronRight"} size={20} />
                                <Text style={[s.spanS, { fontWeight: '600', width: 120, paddingTop: 2 }]}>Est. Weekly Cost</Text>
                            </View>
                            {expandEst ? <View>
                                <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>Rate</Text>
                                <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>Hours/Week</Text>
                                <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>Pieces</Text>
                            </View> : null}
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Icon3 style={{ cursor: 'pointer' }} onPress={() => setExpandedGroupAct(prev => ({ ...prev, [group.name]: !prev[group.name] }))} name={isActExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} />
                                <Text style={[s.spanS, { fontWeight: '600', width: 120 }]}>Act. Weekly Cost</Text>
                            </View>
                            {isActExpanded && group.units.map((unit) => (
                                <View key={unit.code} style={{ flexDirection: 'row', alignItems: 'center', paddingLeft: 16, paddingVertical: 1 }}>
                                    <Text style={{ fontSize: 10, fontWeight: '600', color: '#1e293b', marginLeft: 2 }}>{unit.code}</Text>
                                    <Text numberOfLines={1} style={{ fontSize: 10, color: '#64748b', marginLeft: 4, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{unit.description}</Text>
                                </View>
                            ))}
                        </View>
                        {/* Left expand buttons */}
                        <View style={{ flexDirection: 'column', gap: 2, zIndex: 12 }}>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 'all')}><Text style={expandBtnSmallText(canGrowLeft)}>{`<`}</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 5)}><Text style={expandBtnSmallText(canGrowLeft)}>+5</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 2)}><Text style={expandBtnSmallText(canGrowLeft)}>+2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -2)}><Text style={expandBtnSmallText(canShrinkLeft)}>-2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -5)}><Text style={expandBtnSmallText(canShrinkLeft)}>-5</Text></Pressable>
                        </View>
                    </View>
                    {/* Week columns */}
                    {activeWeeks.map((week, index) => {
                        const wd = weekRow(dataRows, rowId + '=' + week);
                        // Key on all three fields so an "apply across weeks" caret (rate, hrs, OR
                        // pieces) re-mounts the cell and its inputs re-seed from the new data.
                        // Keying on Rate alone left Hrs/Pieces applies invisible on rows that
                        // already had a Rate. dataRows only changes on group applies/reloads —
                        // not on per-week typing — so this never remounts mid-edit.
                        return (
                            <View key={week + '|' + (wd?.Rate ?? '') + '|' + (wd?.Number_Hours ?? '') + '|' + (wd?.Number_Units ?? '')}>
                                <WeekValue week={week} data={wd} />
                            </View>
                        );
                    })}
                    {/* Right expand buttons */}
                    <View style={{ flexDirection: 'column', gap: 2, position: 'sticky', right: 0, zIndex: 12 }}>
                        <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -2)}><Text style={expandBtnSmallText(canShrinkRight)}>-2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -5)}><Text style={expandBtnSmallText(canShrinkRight)}>-5</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 2)}><Text style={expandBtnSmallText(canGrowRight)}>+2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 5)}><Text style={expandBtnSmallText(canGrowRight)}>+5</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 'all')}><Text style={expandBtnSmallText(canGrowRight)}>{`>`}</Text></Pressable>
                    </View>
                </View>
            </View>
        );
    };

    // ── Computed values ──
    const forecastWeek = moment(versionInfo?.Version_Start_DateTime).add(-7, 'days');

    const prioritizedUnits = [...(unitDescriptions || [])].sort((a, b) => {
        const aClass = (a.classification || 'ZZZ_Uncategorized').toLowerCase();
        const bClass = (b.classification || 'ZZZ_Uncategorized').toLowerCase();
        if (aClass !== bClass) return aClass.localeCompare(bClass);
        const aHasData = (pNum(estGroupTotals[a.classification]) + pNum(actualUnitTotals[a.code])) > 0 ? 1 : 0;
        const bHasData = (pNum(estGroupTotals[b.classification]) + pNum(actualUnitTotals[b.code])) > 0 ? 1 : 0;
        if (bHasData !== aHasData) return bHasData - aHasData;
        return a.code.localeCompare(b.code);
    });

    const groupedUnits = React.useMemo(() => {
        const groups = [];
        let currentGroup = null;
        prioritizedUnits.forEach(unit => {
            const groupName = unit.classification || 'Uncategorized';
            if (!currentGroup || currentGroup.name !== groupName) {
                currentGroup = { name: groupName, classificationDescription: unit.classificationDescription || '', units: [] };
                groups.push(currentGroup);
            }
            currentGroup.units.push(unit);
        });
        groups.forEach(g => {
            g.weekActuals = {};
            g.weekEtc = {};
            g.units.forEach(u => {
                projectWeeks.forEach(week => {
                    const actual = actualUnitWeekDollars[u.code]?.[week] || 0;
                    if (actual) g.weekActuals[week] = (g.weekActuals[week] || 0) + actual;
                });
            });
            projectWeeks.forEach(week => {
                const w = userData && userData[g.name + '=' + week];
                if (w && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(completionDateForecast))) {
                    const etc = pNum(w.Number_Hours) * pNum(w.Rate) * pNum(w.Number_Units);
                    if (etc) g.weekEtc[week] = etc;
                }
            });
        });
        return groups;
    }, [prioritizedUnits, projectWeeks, actualUnitWeekDollars, userData, versionInfo, completionDateForecast]);

    const toggleGroupCollapse = (groupName) => {
        setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    const visibleWeeks = React.useMemo(() => {
        if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return projectWeeksCloseToToday;
        const todayWeek = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek');
        const lastProjectWeek = moment(projectWeeks[projectWeeks.length - 1]);
        const jobFinished = lastProjectWeek.isBefore(todayWeek, 'week');
        if (jobFinished) {
            let lastDataIdx = projectWeeks.length - 1;
            for (let i = projectWeeks.length - 1; i >= 0; i--) {
                const wd = projectWeeksData[projectWeeks[i]];
                if (wd && (pNum(wd.actualDollars) > 0 || pNum(wd.etcDollars) > 0)) { lastDataIdx = i; break; }
            }
            const rangeSize = 15 + weekRangeLeft + weekRangeRight;
            return projectWeeks.slice(Math.max(0, lastDataIdx - rangeSize + 1), lastDataIdx + 1);
        }
        const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(todayWeek, 'week'));
        if (todayIdx === -1) return projectWeeksCloseToToday;
        const startIdx = Math.max(0, todayIdx - (5 + weekRangeLeft));
        const endIdx = Math.min(projectWeeks.length - 1, todayIdx + (10 + weekRangeRight));
        return projectWeeks.slice(startIdx, endIdx + 1);
    }, [projectWeeks, projectWeeksCloseToToday, weekRangeLeft, weekRangeRight, versionInfo, projectWeeksData]);

    // Single source of truth for which weeks are active — used everywhere
    const activeWeeks = expandedWeeks ? projectWeeks : visibleWeeks;

    const canShrinkLeft = weekRangeLeft > 0 || expandedWeeks;
    const canShrinkRight = weekRangeRight > 0 || expandedWeeks;
    const canGrowLeft = expandedWeeks ? false : (() => { if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return false; const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week')); return todayIdx > 0 && (todayIdx - (5 + weekRangeLeft)) > 0; })();
    const canGrowRight = expandedWeeks ? false : (() => { if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return false; const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week')); return todayIdx >= 0 && (todayIdx + 10 + weekRangeRight) < projectWeeks.length - 1; })();

    const adjustWeekRange = (side, delta) => {
        if (expandedWeeks) { setExpandedWeeks(false); setWeekRangeLeft(0); setWeekRangeRight(0); return; }
        if (side === 'left') {
            if (delta === 'all') {
                const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week'));
                setWeekRangeLeft(Math.max(0, todayIdx - 5));
            } else { setWeekRangeLeft(prev => Math.max(0, prev + delta)); }
        } else {
            if (delta === 'all') {
                const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week'));
                setWeekRangeRight(Math.max(0, projectWeeks.length - 1 - todayIdx - 10));
            } else { setWeekRangeRight(prev => Math.max(0, prev + delta)); }
        }
    };

    // Per-phase rows for the top Cost expander: budget/COs from saved phase totals,
    // actuals from cost history Phase_Code, ETC attributed via each group's saved phase
    const phaseBreakdownRows = (() => {
        const phases = {};
        const touch = (code) => {
            const key = code || 'Unassigned';
            if (!phases[key]) phases[key] = { code: key, budget: 0, co: 0, actuals: 0, etc: 0 };
            return phases[key];
        };
        Object.keys(dayZeroPhaseTotals).forEach(p => { touch(p).budget += pNum(dayZeroPhaseTotals[p]); });
        Object.keys(coPhaseTotals).forEach(p => { touch(p).co += pNum(coPhaseTotals[p]); });
        Object.keys(actualPhaseTotals).forEach(p => { touch(p).actuals += pNum(actualPhaseTotals[p]); });
        Object.keys(estGroupTotals || {}).forEach(g => {
            touch(userData?.[g]?.Cost_Code || '100102').etc += pNum(estGroupTotals[g]);
        });
        return Object.values(phases)
            .filter(p => p.budget || p.co || p.actuals || p.etc)
            .sort((a, b) => (a.code + '').localeCompare(b.code + '', undefined, { numeric: true, sensitivity: 'base' }));
    })();

    const expandBtnSmall = (active) => ({ padding: '2px 4px', backgroundColor: active ? '#f1f5f9' : '#f8f9fa', borderRadius: 4, border: active ? '1px solid #e2e8f0' : '1px solid #f1f3f5', cursor: active ? 'pointer' : 'default', zIndex: 12, opacity: active ? 1 : 0.35 });
    const expandBtnSmallText = (active) => ({ fontSize: 10, fontWeight: '600', color: active ? '#475569' : '#94a3b8' });

    const toggleCollapseAll = () => {
        const allCollapsed = groupedUnits.every(g => collapsedGroups[g.name]);
        const newState = {};
        groupedUnits.forEach(g => { newState[g.name] = !allCollapsed; });
        setCollapsedGroups(newState);
    };

    const addNewRow = (nameOverride) => {
        const name = (nameOverride || newRowName || '').trim();
        if (!name) return;
        const rate = equipmentRates?.[name] ? pNum(equipmentRates[name]) : '';
        const defaultPhase = '100102';
        updateUserDataArray({
            userId: userId || '154045', userName: userName || 'Seth Bailey',
            fieldName: 'customGroup', tab: TAB, rowId: 'customGroup:' + name,
            version: selectedVersion + '', value: name, id: selectedJob,
            costType: COST_TYPE, lockedVersion: lockedVersion
        });
        // Save initial rate + phase for the new row header
        updateUserDataArray({
            userId: userId || '154045', userName: userName || 'Seth Bailey',
            fieldName: 'row', tab: TAB, rowId: name,
            version: selectedVersion + '', value: '', id: selectedJob,
            costCode: defaultPhase, costType: COST_TYPE, lockedVersion: lockedVersion,
            rate: rate + '', numberHours: '', numberUnits: ''
        });
        // Update local userData so GroupRow picks up the rate immediately
        setUserData(prev => ({ ...prev, [name]: { Rate: rate + '', Cost_Code: defaultPhase } }));
        setCustomRows(prev => [...prev, { name, classificationDescription: '', units: [], weekActuals: {}, weekEtc: {}, isCustom: true }]);
        setNewRowName('');
    };

    const removeRow = (name) => {
        // Find every Forecast_User_Data row for this custom row in the current job+version
        const matches = (jobUserData || []).filter(d => {
            if (d?.Forecast_Tab !== TAB) return false;
            if (d.BU_ID != selectedJob) return false;
            if (d.Version_GUID != (selectedVersion + '')) return false;
            const rid = d.Forecast_Row_ID || '';
            return (
                rid === 'customGroup:' + name ||
                rid === name ||
                rid.startsWith(name + '=') ||
                rid.startsWith(name + '*')
            );
        });
        // Hard-delete each matched GUID
        matches.forEach(d => {
            deleteUserData({
                id: selectedJob,
                forecastGuid: selectedVersion,
                lockedVersion,
                rowId: d.Forecast_User_Data_GUID + '',
                tab: TAB,
            });
        });
        // Drop from atom + local state so the row disappears immediately
        const deletedGuids = new Set(matches.map(d => d.Forecast_User_Data_GUID));
        setJobUserData(prev => (prev || []).filter(d => !deletedGuids.has(d.Forecast_User_Data_GUID)));
        setUserData(prev => {
            if (!prev) return prev;
            const next = { ...prev };
            delete next[name];
            projectWeeks.forEach(week => { delete next[name + '=' + week]; });
            return next;
        });
        setCustomRows(prev => prev.filter(r => r.name !== name));
    };

    // Lookup of equipment code -> description from the master asset list (label is "code - description").
    // Keyed by both raw and normalized code so user-added rows resolve regardless of how the name was stored.
    const equipmentDescByCode = React.useMemo(() => {
        const map = {};
        (equipmentAssets || []).forEach(a => {
            if (!a?.value) return;
            const desc = a.label && a.label.includes(' - ') ? a.label.substring(a.label.indexOf(' - ') + 3).trim() : '';
            if (desc && desc !== a.value) {
                map[a.value] = desc;
                map[normalizeName(a.value)] = desc;
            }
        });
        return map;
    }, [equipmentAssets]);

    // Populate weekEtc on customRows the same way groupedUnits does, so collapsed view shows per-week estimates
    const customRowsWithData = React.useMemo(() => {
        return customRows.map(row => {
            const weekEtc = {};
            (projectWeeks || []).forEach(week => {
                const w = userData && userData[row.name + '=' + week];
                if (w && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(completionDateForecast))) {
                    const etc = pNum(w.Number_Hours) * pNum(w.Rate) * pNum(w.Number_Units);
                    if (etc) weekEtc[week] = etc;
                }
            });
            const classificationDescription = row.classificationDescription || equipmentDescByCode[row.name] || '';
            return { ...row, weekEtc, classificationDescription };
        });
    }, [customRows, userData, projectWeeks, versionInfo, completionDateForecast, equipmentDescByCode]);

    // Auto-collapse any group not yet tracked — seeds initial load and catches late-arriving customRows
    useEffect(() => {
        setCollapsedGroups(prev => {
            let anyMissing = false;
            const next = { ...prev };
            [...groupedUnits, ...customRows].forEach(g => {
                if (g?.name && !(g.name in next)) {
                    next[g.name] = true;
                    anyMissing = true;
                }
            });
            return anyMissing ? next : prev;
        });
    }, [groupedUnits, customRows]);

    // ── RENDER ──
    return (
        <View style={{ padding: 20, marginBottom: 100 }}>
            {/* Subtab bar: Forecast Grid ↔ Equipment Dashboard */}
            {canSeeWipTabs && (
            <View style={{ flexDirection: 'row', gap: 4, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                {[{ key: 'grid', label: 'Forecast Grid' }, { key: 'dashboard', label: 'Equipment Dashboard' }].filter(t => !isPC || t.key === 'dashboard').map(t => (
                    <Pressable key={t.key} onPress={() => setSubTab(t.key)} style={{
                        paddingTop: 8, paddingBottom: 8, paddingLeft: 14, paddingRight: 14, cursor: 'pointer',
                        borderBottom: activeSubTab === t.key ? '2px solid #1d4ed8' : '2px solid transparent', marginBottom: -1,
                    }}>
                        <Text style={{ fontSize: 13, fontWeight: activeSubTab === t.key ? '700' : '500', color: activeSubTab === t.key ? '#1d4ed8' : '#64748b' }}>{t.label}</Text>
                    </Pressable>
                ))}
            </View>
            )}
            {activeSubTab === 'dashboard' ? (
                <View style={{ gap: 16 }}>
                    <EquipmentDashboard />
                    <ResourcePlanTable
                        title="Resource Plan (Hours)"
                        subtitle="Actual equipment hours by category → classification → unit, from cost history. Click a row to expand."
                        countNoun="category"
                        peopleHeader="UNITS"
                        firstLabel="FIRST CHARGE"
                        lastLabel="LAST CHARGE"
                        emptyText="No equipment charged to this job yet."
                        weeks={activeWeeks}
                        forecastWeek={forecastWeek}
                        jobCostDetail={jobCostDetail}
                        projectDates={projectDates}
                        rowFilter={(row) => { const ct = row.Cost_Type_Code; return !!(ct && ct >= COST_TYPE_MIN && ct <= COST_TYPE_MAX && row.Unit_Number !== TPAY) }}
                        getGroup={(row) => EQUIP_CATEGORY_LABELS[getEquipmentIconKey(row.Equipment_Classification_Tier_Name || row.Unit_Number, row.Equipment_Classification_Tier_Name_Description)] || 'Other Equipment'}
                        getClass={(row) => row.Equipment_Classification_Tier_Name_Description || row.Equipment_Classification_Tier_Name || 'Unknown Type'}
                        getLeaf={(row) => row.Unit_Number ? { id: row.Unit_Number, name: row.Unit_Number } : null}
                    />
                </View>
            ) : (
            <View>
            <View style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <View ref={containerRef} style={stickyHeader}>
                    <View style={{ flexDirection: 'row', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 11, minWidth: LEFT_MIN_W -20 }}>
                        <View>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
                                <Pressable onPress={toggleCollapseAll} style={{ padding: 2, cursor: 'pointer' }}>
                                    <Icon3 name={groupedUnits.every(g => collapsedGroups[g.name]) ? 'MdUnfoldMore' : 'MdUnfoldLess'} size={16} color="#64748b" />
                                </Pressable>
                            </View>
                            <View>
                                <View style={{ flexDirection: 'row' }}>
                                    <View style={{ width: 40 }}>
                                        <Text style={[s.total, { width: 40, maxWidth: 40 }]}>Cost</Text>
                                        <Icon3 style={{ cursor: 'pointer' }} onPress={() => setDollarsExpanded(!dollarsExpanded)} name={dollarsExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} />
                                    </View>
                                    <View><Text style={s.total}>Budget</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{dayZeroDollars}</Text></View>
                                    <View><Text style={s.total}>COs</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{coDollars}</Text></View>
                                    <View><Text style={s.total}>Cur. Bdgt</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{currentBudgetDollars}</Text></View>
                                    <View><Text style={s.total}>Actuals</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{actualDollars}</Text></View>
                                    <View><Text style={s.total}>ETC</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{etcDollars}</Text></View>
                                    <View><Text style={s.total}>Total</Text><Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{totalDollars}</Text></View>
                                    <View><Text style={s.total}>Ov/Under</Text><Text style={[s.total, { backgroundColor: '#f3f4f6', color: pNum(overUnderDollars) > 0 ? '#dc2626' : undefined, fontWeight: pNum(overUnderDollars) > 0 ? '600' : undefined }]}>{overUnderDollars}</Text></View>
                                </View>
                            </View>
                            {dollarsExpanded ? <View>
                                {phaseBreakdownRows.map((ph, i) => {
                                    const curBudget = ph.budget + ph.co;
                                    const total = ph.actuals + ph.etc;
                                    const overUnder = total - curBudget;
                                    return (
                                        <View key={i} style={{ flexDirection: 'row' }}>
                                            <View style={[s.total, { padding: 0, height: 30, overflow: 'hidden', width: 40, maxWidth: 40 }]}>
                                                <Text style={[s.totalS, { fontSize: 12 }]}>{ph.code}</Text>
                                                {phaseCodes?.[ph.code] ? <Text style={s.totalS}>{phaseCodes[ph.code]}</Text> : null}
                                            </View>
                                            <View><Text style={s.total}>{ph.budget ? fNum(ph.budget, 1) : ''}</Text></View>
                                            <View><Text style={s.total}>{ph.co ? fNum(ph.co, 1) : ''}</Text></View>
                                            <View><Text style={s.total}>{curBudget ? fNum(curBudget, 1) : ''}</Text></View>
                                            <View><Text style={s.total}>{ph.actuals ? fNum(ph.actuals, 1) : ''}</Text></View>
                                            <View><Text style={s.total}>{ph.etc ? fNum(ph.etc, 1) : ''}</Text></View>
                                            <View><Text style={s.total}>{total ? fNum(total, 1) : ''}</Text></View>
                                            <View><Text style={[s.total, { color: overUnder > 0 ? '#dc2626' : undefined, fontWeight: overUnder > 0 ? '600' : undefined }]}>{(curBudget || total) ? fNum(overUnder, 1) : ''}</Text></View>
                                        </View>
                                    );
                                })}
                            </View> : null}
                        </View>
                        <View>
                            <Text style={s.spanSC}>
                                <Pressable onPress={reloadUserData}><Icon3 name="MdCalculate" size={15} color="#64748b" style={{ marginRight: 8, cursor: 'pointer' }} /></Pressable>
                            </Text>
                            <Text style={s.spanTop}>Actual $</Text>
                            <Text style={s.spanTop}>Estimate $</Text>
                        </View>
                        <View style={{ flexDirection: 'column', gap: 2, zIndex: 12 }}>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 'all')}><Text style={expandBtnSmallText(canGrowLeft)}>{`<`}</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 5)}><Text style={expandBtnSmallText(canGrowLeft)}>+5</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 2)}><Text style={expandBtnSmallText(canGrowLeft)}>+2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -2)}><Text style={expandBtnSmallText(canShrinkLeft)}>-2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -5)}><Text style={expandBtnSmallText(canShrinkLeft)}>-5</Text></Pressable>
                        </View>
                    </View>
                    {/* Header week columns */}
                    {activeWeeks.map((row, index) => {
                        const wd = projectWeeksData[row];
                        const actVal = pNum(wd?.actualDollars);
                        const estVal = pNum(wd?.etcDollars);
                        return (
                        <View key={index} style={{ borderRight: forecastWeek.isSame(moment(row), 'week') ? '2px solid #1e40af' : 'none' }}>
                            <Text style={[s.rateC, weekDateHeader]}>{row}</Text>
                            <Text style={[s.rate, { backgroundColor: '#f3f4f6', fontWeight: actVal > 0 ? '600' : 'normal', color: actVal > 0 ? '#1f2937' : '#94a3b8', fontSize: 10 }]}>{actVal > 0 ? fNum(actVal, 1) : ''}</Text>
                            <Text style={[s.rate, { backgroundColor: '#f3f4f6', fontWeight: estVal > 0 ? '600' : 'normal', color: estVal > 0 ? '#1f2937' : '#94a3b8', fontSize: 10 }]}>{estVal > 0 ? fNum(estVal, 1) : ''}</Text>
                        </View>
                        );
                    })}
                    <View style={{ flexDirection: 'column', gap: 2, position: 'sticky', right: 0, zIndex: 12 }}>
                        <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -2)}><Text style={expandBtnSmallText(canShrinkRight)}>-2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -5)}><Text style={expandBtnSmallText(canShrinkRight)}>-5</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 2)}><Text style={expandBtnSmallText(canGrowRight)}>+2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 5)}><Text style={expandBtnSmallText(canGrowRight)}>+5</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 'all')}><Text style={expandBtnSmallText(canGrowRight)}>{`>`}</Text></Pressable>
                    </View>
                </View>
                <View style={sectionDivider}></View>
            </View>
            {/* Group rows */}
            {groupedUnits.map((group, gIdx) => (
                <GroupRow key={group.name || gIdx} group={group} gIdx={gIdx} />
            ))}
            {customRowsWithData.map((row, rIdx) => (
                <GroupRow key={'custom-' + rIdx} group={row} gIdx={groupedUnits.length + rIdx} />
            ))}
            {/* Add new row */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, gap: 4, position: 'sticky', left: 0 }}>
                {createElement('select', {
                    value: '',
                    onChange: (e) => { if (e.target.value) addNewRow(e.target.value); },
                    style: { width: 320, height: 28, fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 4, paddingLeft: 4, cursor: 'pointer' }
                }, [
                    createElement('option', { key: '__none', value: '' }, '— Select equipment to add —'),
                    ...(() => {
                        const existingNames = new Set([...groupedUnits.map(g => g.name), ...customRows.map(r => r.name)]);
                        return (equipmentAssets || []).filter(a => !existingNames.has(a.value)).map(a => {
                            const rate = equipmentRates?.[a.value] ? pNum(equipmentRates[a.value]) : 0;
                            const desc = a.label && a.label.includes(' - ') ? a.label.substring(a.label.indexOf(' - ') + 3) : a.label || a.value;
                            return createElement('option', { key: a.value, value: a.value },
                                desc + (rate ? '  ($' + fNum(rate, 2) + '/hr)' : ''));
                        });
                    })()
                ])}
            </View>
            {/* Confirm delete modal */}
            {confirmDelete ? (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                    <Pressable onPress={() => setConfirmDelete(null)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
                    <View style={{ backgroundColor: 'white', borderRadius: 10, padding: 28, maxWidth: 420, width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', zIndex: 1001 }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: '#1e293b', marginBottom: 8 }}>Delete Row</Text>
                        <Text style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>This will delete all estimates for <Text style={{ fontWeight: '700' }}>{confirmDelete}</Text>.</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>The row will still appear if it has actuals.</Text>
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
                            <Pressable onPress={() => setConfirmDelete(null)} style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 6, border: '1px solid #d1d5db', backgroundColor: '#f9fafb', cursor: 'pointer' }}>
                                <Text style={{ fontSize: 13, color: '#475569' }}>Cancel</Text>
                            </Pressable>
                            <Pressable onPress={() => { const name = confirmDelete; setConfirmDelete(null); setTimeout(() => removeRow(name), 0); }} style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 6, backgroundColor: '#ef4444', cursor: 'pointer' }}>
                                <Text style={{ fontSize: 13, color: 'white', fontWeight: '600' }}>Delete</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            ) : null}
            </View>
            )}
            <AuditHistoryPanel tabKey="equipment" />
        </View>
    );
};

export default BurnToolPhasedEquipment;
